import { cn } from '@/shared/utils/cn'
import { useCopyToClipboard } from '@/shared/utils/useCopyToClipboard'
import { Icon } from '@/shared/ui/Icon'
import type { AnswerMode, ConversationMessageDTO } from '@/shared/api'
import { MarkdownMessage } from './MarkdownMessage'
import { MessageActions } from './MessageActions'
import { NoContextActions } from './NoContextActions'

function formatTime(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  } catch {
    return iso
  }
}

/**
 * Step 8.2 — rótulo discreto exibido apenas para modos não-padrão. O modo
 * ``case_only`` é silencioso (default histórico, sem ruído visual). Para
 * ``no_context`` a própria mensagem do assistant já comunica o estado, mas
 * mantemos o chip para consistência com os outros modos não-triviais.
 */
function answerModeBadge(mode: AnswerMode | null): {
  label: string
  hint: string
  tone: 'multi' | 'reference' | 'empty'
} | null {
  if (mode === 'multi_context') {
    return {
      label: 'Caso + Referências',
      hint:
        'Esta resposta combina o caso selecionado com as referências escolhidas.',
      tone: 'multi',
    }
  }
  if (mode === 'reference_only') {
    return {
      label: 'Apenas referências',
      hint:
        'Esta resposta foi gerada apenas a partir das referências — nenhum documento de caso foi selecionado.',
      tone: 'reference',
    }
  }
  if (mode === 'no_context') {
    return {
      label: 'Sem contexto',
      hint:
        'Nenhum trecho relevante foi encontrado. Selecione um caso e/ou referências.',
      tone: 'empty',
    }
  }
  return null
}

/**
 * POLISH 1 — discrete copy affordance for the user's own prompt. Hover-reveal
 * on the left of the (right-aligned) bubble so it never overlaps the text.
 */
function UserPromptCopy({ text }: { text: string }) {
  const { copied, copy } = useCopyToClipboard()
  return (
    <button
      type="button"
      className={cn('user-copy', { copied })}
      title={copied ? 'Copiado' : 'Copiar pergunta'}
      aria-label={copied ? 'Pergunta copiada' : 'Copiar pergunta'}
      onClick={(e) => {
        e.stopPropagation()
        void copy(text)
      }}
    >
      <Icon name={copied ? 'check' : 'copy'} />
    </button>
  )
}

interface Props {
  message: ConversationMessageDTO
  selected?: boolean
  onSelect?: () => void
}

export function MessageBubble({ message, selected, onSelect }: Props) {
  if (message.role === 'user') {
    return (
      <div className="msg user">
        {message.content.trim().length > 0 && (
          <UserPromptCopy text={message.content} />
        )}
        <div className="bubble">{message.content}</div>
      </div>
    )
  }

  const responseSeconds =
    typeof message.latency_ms === 'number' ? message.latency_ms / 1000 : null

  const badge = answerModeBadge(message.answer_mode)
  // Step 13.7 — selo discreto de baixa confiança. Anexado client-side a partir
  // do envelope do POST (não persiste no histórico). Não-alarmante: tom âmbar,
  // explicação no tooltip, fontes seguem visíveis no painel à direita.
  const lowConfidence = message.low_confidence === true

  return (
    <div
      className={cn('msg assistant', { selected: Boolean(selected) })}
      data-testid="assistant-message"
      data-answer-mode={message.answer_mode ?? ''}
      data-low-confidence={lowConfidence ? 'true' : 'false'}
    >
      {(badge || lowConfidence) && (
        <div className="mb-1.5 flex flex-wrap items-center gap-1.5 self-start">
          {badge && (
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-md border px-1.5 py-px text-[10px] font-medium uppercase tracking-[0.08em]',
                badge.tone === 'multi' &&
                  'border-violet/40 bg-violet/[0.12] text-violet',
                badge.tone === 'reference' &&
                  'border-violet/30 bg-violet/[0.08] text-violet',
                badge.tone === 'empty' &&
                  'border-white/10 bg-white/[0.04] text-text-mute',
              )}
              title={badge.hint}
            >
              {badge.label}
            </span>
          )}
          {lowConfidence && (
            <span
              className="inline-flex items-center gap-1 rounded-md border border-amber/40 bg-amber/[0.12] px-1.5 py-px text-[10px] font-medium uppercase tracking-[0.08em] text-amber"
              title="A resposta foi baseada nos documentos selecionados, mas a correspondência semântica foi fraca. Verifique as fontes."
            >
              Baixa confiança
            </span>
          )}
        </div>
      )}
      {/*
        Answer is a plain <div> (not a <button>): markdown emits block + inline
        interactive content (code-copy buttons, links, tables) that cannot live
        inside a <button>. Click still selects the message to drive the sources
        panel (documented affordance); interactive descendants stopPropagation.
      */}
      <div
        className="answer md"
        data-testid="assistant-answer"
        onClick={onSelect}
      >
        <MarkdownMessage content={message.content} />
      </div>
      {message.answer_mode === 'no_context' && <NoContextActions />}
      <MessageActions
        timestampLabel={formatTime(message.created_at)}
        sourceCount={message.sources.length}
        responseSeconds={responseSeconds}
        copyText={message.content}
        messageId={message.id}
      />
    </div>
  )
}
